/**
 * EF-RBAC-COMPLETION-AND-AUDIT-V1: Role Management Page
 * 
 * SuperAdmin-only page for managing platform and tenant roles.
 * Features:
 * - View all roles with permissions
 * - Edit role permissions
 * - Create new custom roles
 * - Disable/delete custom roles
 * - View permission audit logs
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Shield, Plus, Edit, Trash2, RefreshCw, Lock, Unlock,
  Users, FileText, Activity, AlertTriangle, CheckCircle, XCircle
} from 'lucide-react';

// Available permissions grouped by category
const PERMISSION_GROUPS = {
  platform: {
    label: 'Platform',
    permissions: [
      { key: 'platform.tenants.manage', label: 'Manage Tenants' },
      { key: 'platform.tenants.read', label: 'View Tenants' },
      { key: 'platform.tenants.write', label: 'Modify Tenants' },
      { key: 'platform.users.manage', label: 'Manage Users' },
      { key: 'platform.users.read', label: 'View Users' },
      { key: 'platform.system.read', label: 'View System' },
      { key: 'platform.system.write', label: 'Modify System' },
    ],
  },
  crew: {
    label: 'Crew',
    permissions: [
      { key: 'crew.read', label: 'View Crew' },
      { key: 'crew.write', label: 'Manage Crew' },
    ],
  },
  projects: {
    label: 'Projects',
    permissions: [
      { key: 'projects.read', label: 'View Projects' },
      { key: 'projects.write', label: 'Manage Projects' },
    ],
  },
  shifts: {
    label: 'Shifts',
    permissions: [
      { key: 'shifts.read', label: 'View Shifts' },
      { key: 'shifts.write', label: 'Manage Shifts' },
      { key: 'shifts.assign', label: 'Assign Crew' },
    ],
  },
  documents: {
    label: 'Documents',
    permissions: [
      { key: 'documents.read', label: 'View Documents' },
      { key: 'documents.write', label: 'Manage Documents' },
      { key: 'documents.upload', label: 'Upload Documents' },
    ],
  },
  finance: {
    label: 'Finance',
    permissions: [
      { key: 'finance.read', label: 'View Finance' },
      { key: 'finance.write', label: 'Manage Finance' },
    ],
  },
};

export default function RolesPage() {
  const { user } = useAuth();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState(null);
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [showAuditDialog, setShowAuditDialog] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditSummary, setAuditSummary] = useState(null);
  const [isCreating, setIsCreating] = useState(false);

  const [roleForm, setRoleForm] = useState({
    name: '',
    display_name: '',
    description: '',
    permissions: [],
    is_platform_role: false,
  });

  // Fetch roles
  const fetchRoles = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/roles');
      setRoles(response.data);
    } catch (error) {
      console.error('Failed to fetch roles:', error);
      toast.error('Failed to load roles');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch audit logs
  const fetchAuditLogs = async () => {
    try {
      const [logsRes, summaryRes] = await Promise.all([
        api.get('/admin/permission-audit-logs?limit=50'),
        api.get('/admin/permission-audit-logs/summary?days=7'),
      ]);
      setAuditLogs(logsRes.data.logs || []);
      setAuditSummary(summaryRes.data);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
      toast.error('Failed to load audit logs');
    }
  };

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  // Handle role selection for editing
  const handleEditRole = (role) => {
    setSelectedRole(role);
    setRoleForm({
      name: role.name,
      display_name: role.display_name || '',
      description: role.description || '',
      permissions: role.permissions || [],
      is_platform_role: role.is_platform_role || false,
    });
    setIsCreating(false);
    setShowRoleDialog(true);
  };

  // Handle new role creation
  const handleNewRole = () => {
    setSelectedRole(null);
    setRoleForm({
      name: '',
      display_name: '',
      description: '',
      permissions: [],
      is_platform_role: false,
    });
    setIsCreating(true);
    setShowRoleDialog(true);
  };

  // Toggle permission in form
  const togglePermission = (permission) => {
    setRoleForm((prev) => {
      const permissions = prev.permissions.includes(permission)
        ? prev.permissions.filter((p) => p !== permission)
        : [...prev.permissions, permission];
      return { ...prev, permissions };
    });
  };

  // Save role (create or update)
  const handleSaveRole = async () => {
    try {
      if (isCreating) {
        // Create new role
        if (!roleForm.name || !roleForm.display_name) {
          toast.error('Name and display name are required');
          return;
        }
        await api.post('/admin/roles', roleForm);
        toast.success(`Role "${roleForm.display_name}" created successfully`);
      } else {
        // Update existing role
        await api.patch(`/admin/roles/${selectedRole.name}`, {
          display_name: roleForm.display_name,
          description: roleForm.description,
          permissions: roleForm.permissions,
        });
        toast.success(`Role "${roleForm.display_name}" updated successfully`);
      }
      setShowRoleDialog(false);
      fetchRoles();
    } catch (error) {
      console.error('Failed to save role:', error);
      toast.error(error.response?.data?.detail || 'Failed to save role');
    }
  };

  // Delete role
  const handleDeleteRole = async (roleName) => {
    if (!confirm(`Are you sure you want to delete the role "${roleName}"?`)) {
      return;
    }
    try {
      await api.delete(`/admin/roles/${roleName}`);
      toast.success(`Role "${roleName}" deleted successfully`);
      fetchRoles();
    } catch (error) {
      console.error('Failed to delete role:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete role');
    }
  };

  // Check if user is superadmin
  if (user?.role !== 'superadmin') {
    return (
      <div className="p-8 text-center">
        <AlertTriangle className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-white mb-2">Access Denied</h2>
        <p className="text-gray-400">Only SuperAdmins can access this page.</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="roles-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Shield className="h-6 w-6 text-purple-400" />
            Role Management
          </h1>
          <p className="text-gray-400 mt-1">Manage platform and tenant roles with permissions</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchRoles} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={() => { fetchAuditLogs(); setShowAuditDialog(true); }}>
            <Activity className="h-4 w-4 mr-2" />
            Audit Logs
          </Button>
          <Button onClick={handleNewRole}>
            <Plus className="h-4 w-4 mr-2" />
            New Role
          </Button>
        </div>
      </div>

      {/* Tabs for Platform and Tenant Roles */}
      <Tabs defaultValue="all" className="w-full">
        <TabsList>
          <TabsTrigger value="all">All Roles</TabsTrigger>
          <TabsTrigger value="platform">Platform Roles</TabsTrigger>
          <TabsTrigger value="tenant">Tenant Roles</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-4">
          <RolesTable 
            roles={roles} 
            onEdit={handleEditRole} 
            onDelete={handleDeleteRole}
          />
        </TabsContent>

        <TabsContent value="platform" className="mt-4">
          <RolesTable 
            roles={roles.filter(r => r.is_platform_role)} 
            onEdit={handleEditRole} 
            onDelete={handleDeleteRole}
          />
        </TabsContent>

        <TabsContent value="tenant" className="mt-4">
          <RolesTable 
            roles={roles.filter(r => !r.is_platform_role)} 
            onEdit={handleEditRole} 
            onDelete={handleDeleteRole}
          />
        </TabsContent>
      </Tabs>

      {/* Edit/Create Role Dialog */}
      <Dialog open={showRoleDialog} onOpenChange={setShowRoleDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {isCreating ? 'Create New Role' : `Edit Role: ${selectedRole?.display_name}`}
            </DialogTitle>
            <DialogDescription>
              {isCreating 
                ? 'Create a new custom role with specific permissions.'
                : 'Modify the permissions for this role. System roles cannot be deleted.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name (ID) {isCreating && '*'}</Label>
                <Input
                  value={roleForm.name}
                  onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                  placeholder="custom_role"
                  disabled={!isCreating}
                  data-testid="role-name-input"
                />
              </div>
              <div>
                <Label>Display Name *</Label>
                <Input
                  value={roleForm.display_name}
                  onChange={(e) => setRoleForm({ ...roleForm, display_name: e.target.value })}
                  placeholder="Custom Role"
                  data-testid="role-display-name-input"
                />
              </div>
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={roleForm.description}
                onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
                placeholder="Describe the purpose of this role..."
                rows={2}
              />
            </div>

            {isCreating && (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="is_platform_role"
                  checked={roleForm.is_platform_role}
                  onCheckedChange={(checked) => setRoleForm({ ...roleForm, is_platform_role: checked })}
                />
                <Label htmlFor="is_platform_role">Platform Role (cross-tenant access)</Label>
              </div>
            )}

            {/* Permissions */}
            <div>
              <Label className="text-base font-semibold mb-3 block">Permissions</Label>
              <div className="space-y-4">
                {Object.entries(PERMISSION_GROUPS).map(([groupKey, group]) => (
                  <Card key={groupKey} className="bg-background/50">
                    <CardHeader className="py-3">
                      <CardTitle className="text-sm">{group.label}</CardTitle>
                    </CardHeader>
                    <CardContent className="py-2">
                      <div className="grid grid-cols-2 gap-2">
                        {group.permissions.map((perm) => (
                          <div key={perm.key} className="flex items-center space-x-2">
                            <Checkbox
                              id={perm.key}
                              checked={roleForm.permissions.includes(perm.key)}
                              onCheckedChange={() => togglePermission(perm.key)}
                            />
                            <Label htmlFor={perm.key} className="text-sm font-normal cursor-pointer">
                              {perm.label}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Selected Permissions Summary */}
            <div className="bg-muted/30 rounded-lg p-3">
              <Label className="text-xs text-gray-400">Selected Permissions ({roleForm.permissions.length})</Label>
              <div className="flex flex-wrap gap-1 mt-2">
                {roleForm.permissions.length === 0 ? (
                  <span className="text-gray-500 text-sm">No permissions selected</span>
                ) : (
                  roleForm.permissions.map((p) => (
                    <Badge key={p} variant="secondary" className="text-xs">
                      {p}
                    </Badge>
                  ))
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRoleDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveRole} data-testid="save-role-btn">
              {isCreating ? 'Create Role' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audit Logs Dialog */}
      <Dialog open={showAuditDialog} onOpenChange={setShowAuditDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Permission Audit Logs</DialogTitle>
            <DialogDescription>
              Track all permission checks across the platform (last 7 days)
            </DialogDescription>
          </DialogHeader>

          {auditSummary && (
            <div className="grid grid-cols-4 gap-4 py-4">
              <Card className="bg-background/50">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-white">{auditSummary.total_checks}</div>
                  <div className="text-xs text-gray-400">Total Checks</div>
                </CardContent>
              </Card>
              <Card className="bg-background/50">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-green-400">
                    {auditSummary.total_checks - auditSummary.total_denied}
                  </div>
                  <div className="text-xs text-gray-400">Allowed</div>
                </CardContent>
              </Card>
              <Card className="bg-background/50">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-red-400">{auditSummary.total_denied}</div>
                  <div className="text-xs text-gray-400">Denied</div>
                </CardContent>
              </Card>
              <Card className="bg-background/50">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-blue-400">
                    {auditSummary.by_permission?.length || 0}
                  </div>
                  <div className="text-xs text-gray-400">Unique Permissions</div>
                </CardContent>
              </Card>
            </div>
          )}

          <div className="max-h-[400px] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Permission</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Result</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {auditLogs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell className="text-xs">
                      {new Date(log.timestamp).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">{log.email}</div>
                      <div className="text-xs text-gray-500">{log.role}</div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">{log.permission}</Badge>
                    </TableCell>
                    <TableCell className="text-sm">{log.action}</TableCell>
                    <TableCell>
                      {log.success ? (
                        <CheckCircle className="h-4 w-4 text-green-400" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-400" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {auditLogs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-gray-500 py-8">
                      No audit logs found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAuditDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Roles Table Component
function RolesTable({ roles, onEdit, onDelete }) {
  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Role</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Permissions</TableHead>
              <TableHead>System</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {roles.map((role) => (
              <TableRow key={role.name} data-testid={`role-row-${role.name}`}>
                <TableCell>
                  <div className="font-medium text-white">{role.display_name || role.name}</div>
                  <div className="text-xs text-gray-500">{role.name}</div>
                  {role.description && (
                    <div className="text-xs text-gray-400 mt-1">{role.description}</div>
                  )}
                </TableCell>
                <TableCell>
                  <Badge className={role.is_platform_role ? 'bg-purple-500/20 text-purple-400' : 'bg-blue-500/20 text-blue-400'}>
                    {role.is_platform_role ? 'Platform' : 'Tenant'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1 max-w-md">
                    {(role.permissions || []).slice(0, 5).map((p) => (
                      <Badge key={p} variant="outline" className="text-xs">
                        {p}
                      </Badge>
                    ))}
                    {(role.permissions || []).length > 5 && (
                      <Badge variant="secondary" className="text-xs">
                        +{role.permissions.length - 5} more
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  {role.is_system_role ? (
                    <Lock className="h-4 w-4 text-gray-500" title="System role (cannot delete)" />
                  ) : (
                    <Unlock className="h-4 w-4 text-green-400" title="Custom role" />
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onEdit(role)}
                      data-testid={`edit-role-${role.name}`}
                    >
                      <Edit className="h-3 w-3" />
                    </Button>
                    {!role.is_system_role && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-400 hover:text-red-300"
                        onClick={() => onDelete(role.name)}
                        data-testid={`delete-role-${role.name}`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {roles.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-gray-500 py-8">
                  No roles found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
